"use client";

import { useEffect, useMemo, useState, startTransition } from "react";
import { GiftIcon, ImageIcon, SparklesIcon, ZapIcon } from "lucide-react";

import { WhatsAppIcon } from "@/app/components/icons/WhatsAppIcon";
import { PHOTO_PACK_CATALOG } from "@/src/core/domain/types";
import { PHOTO_PACK_PRICES } from "@/src/core/packs/pack-pricing";
import { getPhotosPerRound } from "@/src/core/packs/pack-policy";
import { trackEvent } from "@/src/lib/gtm-track";

const PACKS = [
  {
    tier: "pack_6" as const,
    emoji: "✨",
  },
  {
    tier: "pack_12" as const,
    emoji: "🔥",
    highlight: "choice" as const,
  },
  {
    tier: "pack_24" as const,
    emoji: "🚀",
    highlight: "best" as const,
  },
];

const WA_PHONE = "16812992414";

function buildPackWhatsappHref(packPhotos: number) {
  const body = `Hola! Quiero mi pack de ${packPhotos} fotos`;
  return `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(body)}`;
}

function formatUsd(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function resolveLandingPromoPercentOff(): number {
  const raw = process.env.NEXT_PUBLIC_LANDING_PROMO_PERCENT_OFF ?? process.env.LANDING_PROMO_PERCENT_OFF ?? "0";
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.min(99, Math.round(parsed)));
}

function applyPercentOff(input: { amount: number; percentOff: number; currency: "usd" | "ars" }) {
  const percent = Math.max(0, Math.min(99, Math.round(input.percentOff)));
  const base = input.amount;
  const rawDiscount = (base * percent) / 100;
  const discount = input.currency === "usd" ? Math.round(rawDiscount * 100) / 100 : Math.round(rawDiscount);
  const finalRaw = Math.max(0, base - discount);
  const final = input.currency === "usd" ? Math.round(finalRaw * 100) / 100 : finalRaw;
  return { base, final, percentOff: percent, hasPromo: percent > 0 && final !== base };
}

export const Pricing = () => {
  const photosPerRound = getPhotosPerRound();
  const landingPromoPercentOff = resolveLandingPromoPercentOff();
  const [currency, setCurrency] = useState<"usd" | "ars">("usd");

  useEffect(() => {
    let cancelled = false;
    fetch("/api/geo")
      .then((res) => res.json() as Promise<{ country: string | null }>)
      .then((data) => {
        if (cancelled) return;
        const country = (data.country ?? "").toUpperCase();
        startTransition(() => {
          setCurrency(country === "AR" ? "ars" : "usd");
        });
      })
      .catch(() => {
        // Keep default USD if geo lookup fails.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const currencyLabel = useMemo(() => (currency === "ars" ? "ARS" : "USD"), [currency]);

  function handlePackCtaClick(tier: string) {
    const planName = tier === "pack_6" ? "basic" : tier === "pack_12" ? "classic" : "premium";
    trackEvent({ name: "click_plan_button", params: { plan_name: planName } });
  }

  return (
    <section
      id="precios"
      className="relative w-full scroll-mt-28 overflow-hidden rounded-3xl border border-white/10 bg-zinc-950 px-5 py-10 sm:px-8 lg:px-12 lg:py-14"
    >
      <div className="pointer-events-none absolute -left-32 top-0 h-64 w-64 rounded-full bg-cyan-500/20 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-violet-600/20 blur-3xl" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-fuchsia-500/5 blur-3xl" />

      <div className="relative z-10">
        <div className="flex flex-col items-start gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-violet-300/35 bg-violet-500/10 px-3 py-1 text-xs font-semibold tracking-wide text-violet-200">
              <ImageIcon className="h-3.5 w-3.5" />
              Precios claros, sin sorpresas
            </div>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-white sm:text-4xl lg:text-5xl">
              Elegís tu pack.
              <span className="block text-cyan-300">Recibís tus fotos en HD.</span>
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-300 sm:text-base">

              Sin suscripciones ni costos ocultos.
            </p>
          </div>

          <div className="rounded-2xl border border-cyan-300/25 bg-linear-to-br from-cyan-400/15 to-violet-500/10 px-4 py-3 text-sm text-cyan-50">
            <p className="flex items-center gap-2 font-semibold text-cyan-100">
              <GiftIcon className="h-4 w-4 shrink-0" />
              1 foto de MUESTRA al empezar
            </p>
            <p className="mt-1 text-xs text-cyan-100/80">
              Nuevos usuarios · marca de agua · solo la primera vez
            </p>
          </div>
        </div>

        <div className="mt-7 flex flex-col w-fit gap-3 rounded-2xl border border-white/10 bg-white/3 px-4 py-3.5 backdrop-blur-sm sm:mt-8 sm:px-5 sm:py-4">
          <div className="flex items-center gap-2">
            <SparklesIcon className="mt-0.5 h-5 w-5 shrink-0 text-violet-300/90" aria-hidden />
            <p className="text-sm leading-relaxed text-zinc-300">
              Cada pack incluye{" "}
              <span className="font-semibold text-zinc-100">{photosPerRound} variantes por ronda</span>
            </p>

          </div>
          <ul className="list-disc list-inside text-xs">
            <li>Pack 6 = 1 ronda de 6 fotos</li>
            <li>Pack 12 = 2 rondas de 6 fotos</li>
            <li>Pack 24 = 4 rondas de 6 fotos</li>
          </ul>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-3 lg:gap-6">
          {PACKS.map((pack) => {
            const catalog = PHOTO_PACK_CATALOG[pack.tier];
            const prices = PHOTO_PACK_PRICES[pack.tier];
            const usdQuote = applyPercentOff({
              amount: prices.usd,
              percentOff: landingPromoPercentOff,
              currency: "usd",
            });
            const arsQuote = applyPercentOff({
              amount: prices.ars,
              percentOff: landingPromoPercentOff,
              currency: "ars",
            });
            const isBest = "highlight" in pack && pack.highlight === "best";
            const isChoice = "highlight" in pack && pack.highlight === "choice";

            return (
              <article
                key={pack.tier}
                className={[
                  "relative flex flex-col rounded-2xl border p-5 transition duration-300",
                  isChoice
                    ? "z-1 border-amber-400/45 bg-linear-to-b from-amber-500/[0.14] via-violet-950/50 to-zinc-950 shadow-xl shadow-amber-500/20 ring-2 ring-amber-400/40 lg:-my-1 lg:scale-[1.045] lg:p-6"
                    : isBest
                      ? "border-cyan-400/40 bg-linear-to-b from-cyan-500/12 via-zinc-900/85 to-zinc-950 shadow-md shadow-cyan-500/10 ring-1 ring-cyan-400/25 lg:p-5"
                      : "border-white/10 bg-white/3 hover:border-white/20 hover:bg-white/5",
                ].join(" ")}
              >
                {isChoice && (
                  <div className="absolute -top-3.5 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-0.5">
                    <span className="whitespace-nowrap rounded-full bg-linear-to-r from-amber-400 via-orange-300 to-violet-500 px-4 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-zinc-950 shadow-lg shadow-amber-500/30 sm:text-xs sm:tracking-[0.22em]">
                      Pack recomendado
                    </span>
                    <span className="hidden text-[10px] font-semibold uppercase tracking-wide text-amber-200/90 sm:block">
                      12 fotos · equilibrio ideal
                    </span>
                  </div>
                )}
                {isBest && !isChoice && (
                  <div className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full border border-cyan-400/35 bg-cyan-950/80 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-cyan-100 shadow-md backdrop-blur-sm sm:text-xs">
                    Mejor relación precio
                  </div>
                )}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="text-2xl" aria-hidden>
                      {pack.emoji}
                    </span>
                    <h3
                      className={
                        isChoice
                          ? "mt-2 bg-linear-to-r from-amber-100 via-white to-violet-200 bg-clip-text text-2xl font-black tracking-tight text-transparent sm:text-3xl"
                          : "mt-2 text-lg font-bold text-white"
                      }
                    >
                      {isChoice ? `Pack de ${catalog.photos} fotos` : `${catalog.photos} fotos`}
                    </h3>
                    <p
                      className={
                        isChoice ? "mt-1 text-sm font-medium text-zinc-300" : "text-sm text-zinc-400"
                      }
                    >
                      {catalog.rounds} {catalog.rounds === 1 ? "ronda" : "rondas"} · de 6 fotos
                    </p>
                  </div>
                  <ZapIcon
                    className={
                      isChoice
                        ? "h-6 w-6 shrink-0 text-amber-400 sm:h-7 sm:w-7"
                        : "h-5 w-5 shrink-0 text-violet-400/80"
                    }
                    aria-hidden
                  />
                </div>

                <div className="mt-5 flex flex-col gap-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    {currency === "usd" && (
                      <>
                        {usdQuote.hasPromo && (
                          <span className="text-sm font-semibold text-zinc-400 line-through">
                            {formatUsd(usdQuote.base)}
                          </span>
                        )}
                        <span className="text-3xl font-black tracking-tight text-white sm:text-4xl">
                          {formatUsd(usdQuote.final)}
                        </span>
                        <span className="text-xs font-semibold uppercase tracking-wide text-cyan-300/90">
                          USD
                        </span>
                        {usdQuote.hasPromo && (
                          <span className="inline-flex max-w-full items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-400/15 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-emerald-200">
                            <GiftIcon className="h-3 w-3 shrink-0 opacity-90" aria-hidden />
                            <span className="min-w-0 truncate">Lanzamiento</span>
                            <span className="shrink-0 text-emerald-200/75" aria-hidden>
                              ·
                            </span>
                            <span className="shrink-0">-{usdQuote.percentOff}%</span>
                          </span>
                        )}
                      </>
                    )}

                    {currency === "ars" && (
                      <>
                        {arsQuote.hasPromo && (
                          <span className="text-sm font-semibold text-zinc-400 line-through">
                            ARS ${arsQuote.base.toLocaleString("es")}
                          </span>
                        )}
                        <span className="text-3xl font-black tracking-tight text-white sm:text-4xl">
                          ARS ${arsQuote.final.toLocaleString("es")}
                        </span>
                        <span className="text-xs font-semibold uppercase tracking-wide text-cyan-300/90">
                          ARS
                        </span>
                        {arsQuote.hasPromo && (
                          <span className="inline-flex max-w-full items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-400/15 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-emerald-200">
                            <GiftIcon className="h-3 w-3 shrink-0 opacity-90" aria-hidden />
                            <span className="min-w-0 truncate">Lanzamiento</span>
                            <span className="shrink-0 text-emerald-200/75" aria-hidden>
                              ·
                            </span>
                            <span className="shrink-0">-{arsQuote.percentOff}%</span>
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <p className="text-sm text-zinc-400">Fotos en HD · entrega por WhatsApp · {currencyLabel}</p>
                </div>

                <p className="mt-4 text-xs leading-relaxed text-zinc-400">
                  {currency === "usd"
                    ? `≈ ${formatUsd(usdQuote.final / catalog.photos)} por foto`
                    : `≈ ARS ${(arsQuote.final / catalog.photos).toLocaleString("es")} por foto`}
                </p>

                <div className="mt-auto pt-6">
                  <a
                    href={buildPackWhatsappHref(catalog.photos)}
                    onClick={() => handlePackCtaClick(pack.tier)}
                    className={
                      isChoice
                        ? "flex w-full items-center justify-center gap-2 rounded-xl bg-linear-to-r from-amber-500 to-violet-600 py-3 text-sm font-bold text-white shadow-lg shadow-amber-500/25 transition hover:brightness-110 sm:text-base"
                        : "flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 py-2.5 text-sm font-semibold text-zinc-100 transition hover:bg-white/10"
                    }
                  >
                    <WhatsAppIcon className="h-6 w-6 shrink-0" />
                    Elegir en WhatsApp
                  </a>
                </div>
              </article>
            );
          })}
        </div>

        {/* <div className="mt-10 flex flex-col items-center gap-4 rounded-2xl border border-white/10 bg-zinc-900/50 px-5 py-6 text-center sm:flex-row sm:justify-between sm:text-left">
          <p className="max-w-xl text-sm text-zinc-400">
            Los montos en pantalla son referencia en USD. El checkout final muestra moneda y total
            según tu país y método de pago. Argentina usa Mercado Pago en pesos; el resto PayPal o
            Stripe en USD.
          </p>
          <a
            href={buildPackWhatsappHref(getPhotosPerRound())}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-bold text-white transition hover:bg-green-500"
          >
            <WhatsAppIcon className="h-6 w-6 shrink-0" />
            Comprar por WhatsApp
          </a>
        </div> */}
      </div>
    </section>
  );
};
